import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  IconButton,
  Avatar,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import HistoryIcon from "@mui/icons-material/History";
import SettingsIcon from "@mui/icons-material/Settings";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BarChartIcon from "@mui/icons-material/BarChart";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import ForumIcon from "@mui/icons-material/Forum";
import CloseIcon from "@mui/icons-material/Close";
import CreditCardIcon from "@mui/icons-material/CreditCard";
// Import the Trade Execution Widget component
import TradeExecutionWidget from "../../components/widgets/TradeExecutionWidget";

// Mock data for user dashboard
const userData = {
  name: "John Doe",
  email: "john.doe@example.com",
  avatar: "https://mui.com/static/images/avatar/1.jpg",
  plan: "Pro",
  planExpiry: "2025-09-15",
  accountBalance: "$4,821.50",
  activeBots: 3,
  totalStrategies: 8,
  dailyProfitLoss: "+$142.87",
  monthlyProfitLoss: "+$1,253.64",
  allTimeProfitLoss: "+$6,489.21",
  notifications: 4,
  usagePercentage: 68,
};

// Mock data for recent trades
const recentTrades = [
  {
    id: 1,
    symbol: "BTC/USD",
    type: "BUY",
    amount: "0.15 BTC",
    price: "$37,842.12",
    time: "2 hours ago",
    status: "Completed",
    profit: "+$124.56",
  },
  {
    id: 2,
    symbol: "ETH/USD",
    type: "SELL",
    amount: "1.2 ETH",
    price: "$2,784.63",
    time: "5 hours ago",
    status: "Completed",
    profit: "+$32.18",
  },
  {
    id: 3,
    symbol: "AAPL",
    type: "BUY",
    amount: "5 shares",
    price: "$187.35",
    time: "1 day ago",
    status: "Completed",
    profit: "-$18.45",
  },
  {
    id: 4,
    symbol: "GOOGL",
    type: "SELL",
    amount: "2 shares",
    price: "$2,632.48",
    time: "2 days ago",
    status: "Completed",
    profit: "+$56.23",
  },
];

// Mock data for active strategies
const activeStrategies = [
  {
    id: 1,
    name: "BTC Trend Follower",
    type: "Crypto",
    status: "Running",
    uptime: "15 days",
    performance: "+8.2%",
  },
  {
    id: 2,
    name: "ETH Oscillator",
    type: "Crypto",
    status: "Running",
    uptime: "7 days",
    performance: "+3.5%",
  },
  {
    id: 3,
    name: "Tech Stocks Momentum",
    type: "Stocks",
    status: "Running",
    uptime: "30 days",
    performance: "+12.6%",
  },
];

// Mock data for quick actions
const quickActions = [
  {
    title: "Create Strategy",
    icon: <AutoGraphIcon />,
    link: "/user/strategy-builder",
  },
  { title: "Trading History", icon: <HistoryIcon />, link: "/user/history" },
  { title: "Analytics", icon: <BarChartIcon />, link: "/user/technical" },
  { title: "Account Settings", icon: <SettingsIcon />, link: "/user/settings" },
];

function UserDashboard() {
  const navigate = useNavigate();

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [openTicketDialog, setOpenTicketDialog] = useState(false);
  const [newTicket, setNewTicket] = useState({ subject: "", description: "" });
  const [showPromotion, setShowPromotion] = useState(true);

  useEffect(() => {
    // Check authentication status
    const authStatus = localStorage.getItem("isAuthenticated");
    if (authStatus !== "true") {
      navigate("/login");
    } else {
      setIsAuthenticated(true);
    }

    // You would typically fetch user data from an API here
  }, [navigate]);

  const handleTicketSubmit = () => {
    // In a real app, you would submit the ticket to your backend here
    // For now, just close the dialog and show a success message
    setOpenTicketDialog(false);
    alert("Support ticket submitted successfully! Our team will respond soon.");
    setNewTicket({ subject: "", description: "" });
  };

  const handleTicketChange = (e) => {
    const { name, value } = e.target;
    setNewTicket({
      ...newTicket,
      [name]: value,
    });
  };

  if (!isAuthenticated) {
    return null; // Don't render anything until authentication check is complete
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* Welcome Header */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Box sx={{
            display: "flex", alignItems: "center", p: 2, borderRadius: 2,
            background: 'linear-gradient(135deg, rgba(11,17,32,0.85) 0%, rgba(24,27,33,0.9) 100%)',
            backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid rgba(255,255,255,0.06)',
          }}>
            <Avatar
              src={userData.avatar}
              sx={{ width: 56, height: 56, mr: 2, border: '2px solid rgba(0,255,148,0.3)' }}
              alt={userData.name}
            />
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1.3rem', color: 'white' }}>
                Welcome back, {userData.name}
              </Typography>
              <Typography variant="body2" sx={{ color: '#9CA3AF' }}>
                Here's what's happening with your trading account today
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
          sx={{
            display: "flex",
            justifyContent: { xs: "flex-start", md: "flex-end" },
            alignItems: "center",
          }}
        >
          <Button
            variant="contained"
            startIcon={<AutoGraphIcon />}
            onClick={() => navigate("/user/strategy-builder")}
            sx={{ mr: 2 }}
          >
            New Strategy
          </Button>
          <Button
            variant="outlined"
            startIcon={<ForumIcon />}
            onClick={() => setOpenTicketDialog(true)}
          >
            Get Support
          </Button>
        </Grid>
      </Grid>

      {/* Promotion Card - Can be dismissed */}
      {showPromotion && (
        <Paper
          sx={{
            p: 2,
            mb: 4,
            background: "linear-gradient(90deg, #3b82f6 0%, #3b82f6 100%)",
            color: "white",
            position: "relative",
          }}
        >
          <IconButton
            sx={{ position: "absolute", top: 8, right: 8, color: "white" }}
            onClick={() => setShowPromotion(false)}
          >
            <CloseIcon />
          </IconButton>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h6">Upgrade to Enterprise Plan</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Get access to advanced AI features, priority support, and
                unlimited strategies. Boost your trading potential with our most
                powerful tools.
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{ textAlign: { xs: "left", md: "right" } }}
            >
              <Button
                variant="contained"
                color="secondary"
                onClick={() => navigate("/user/plans")}
              >
                View Enterprise Features
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Account Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="h6" gutterBottom>
              Current Plan
            </Typography>
            <Chip label={userData.plan} color="primary" sx={{ mb: 2 }} />
            <Typography variant="body2">
              <strong>Expires:</strong>{" "}
              {new Date(userData.planExpiry).toLocaleDateString()}
            </Typography>
            <Box sx={{ my: 2 }}>
              <Typography variant="body2" gutterBottom>
                Usage
              </Typography>
              <LinearProgress
                variant="determinate"
                value={userData.usagePercentage}
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                {userData.usagePercentage}% of your monthly limit
              </Typography>
            </Box>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => navigate("/user/plans")}
              startIcon={<CreditCardIcon />}
              sx={{ mt: 1 }}
            >
              Manage Subscription
            </Button>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="h6" gutterBottom>
              Account Balance
            </Typography>
            <Typography variant="h4" color="primary" gutterBottom>
              {userData.accountBalance}
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography variant="body2">Daily P&L:</Typography>
              <Typography
                variant="body2"
                color={
                  userData.dailyProfitLoss.includes("+")
                    ? "success.main"
                    : "error.main"
                }
              >
                {userData.dailyProfitLoss}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">Monthly P&L:</Typography>
              <Typography
                variant="body2"
                color={
                  userData.monthlyProfitLoss.includes("+")
                    ? "success.main"
                    : "error.main"
                }
              >
                {userData.monthlyProfitLoss}
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="h6" gutterBottom>
              Active Strategies
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Typography variant="h4" color="primary">
                {userData.activeBots}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                / {userData.totalStrategies} total
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate("/user/strategy-builder")}
              sx={{ mr: 1 }}
            >
              Create New
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate("/user/strategy-builder")}
            >
              View All
            </Button>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <List dense>
              {quickActions.map((action, index) => (
                <ListItem disablePadding key={index}>
                  <ListItemButton onClick={() => navigate(action.link)}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {action.icon}
                    </ListItemIcon>
                    <ListItemText primary={action.title} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>

      {/* Recent Activity and Strategy Performance */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Recent Trades
            </Typography>
            <Box sx={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "8px 16px",
                        borderBottom: "1px solid rgba(224, 224, 224, 1)",
                      }}
                    >
                      Symbol
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "8px 16px",
                        borderBottom: "1px solid rgba(224, 224, 224, 1)",
                      }}
                    >
                      Type
                    </th>
                    <th
                      style={{
                        textAlign: "right",
                        padding: "8px 16px",
                        borderBottom: "1px solid rgba(224, 224, 224, 1)",
                      }}
                    >
                      Amount
                    </th>
                    <th
                      style={{
                        textAlign: "right",
                        padding: "8px 16px",
                        borderBottom: "1px solid rgba(224, 224, 224, 1)",
                      }}
                    >
                      Price
                    </th>
                    <th
                      style={{
                        textAlign: "right",
                        padding: "8px 16px",
                        borderBottom: "1px solid rgba(224, 224, 224, 1)",
                      }}
                    >
                      P&L
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {recentTrades.map((trade) => (
                    <tr key={trade.id}>
                      <td
                        style={{
                          padding: "8px 16px",
                          borderBottom: "1px solid rgba(224, 224, 224, 0.5)",
                        }}
                      >
                        {trade.symbol}
                      </td>
                      <td
                        style={{
                          padding: "8px 16px",
                          borderBottom: "1px solid rgba(224, 224, 224, 0.5)",
                        }}
                      >
                        <Chip
                          label={trade.type}
                          size="small"
                          color={trade.type === "BUY" ? "success" : "error"}
                          variant="outlined"
                        />
                      </td>
                      <td
                        style={{
                          textAlign: "right",
                          padding: "8px 16px",
                          borderBottom: "1px solid rgba(224, 224, 224, 0.5)",
                        }}
                      >
                        {trade.amount}
                      </td>
                      <td
                        style={{
                          textAlign: "right",
                          padding: "8px 16px",
                          borderBottom: "1px solid rgba(224, 224, 224, 0.5)",
                        }}
                      >
                        {trade.price}
                      </td>
                      <td
                        style={{
                          textAlign: "right",
                          padding: "8px 16px",
                          borderBottom: "1px solid rgba(224, 224, 224, 0.5)",
                          color: trade.profit.includes("+")
                            ? "#00FF94"
                            : "#EF4444",
                        }}
                      >
                        {trade.profit}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button
                variant="text"
                endIcon={<HistoryIcon />}
                onClick={() => navigate("/user/history")}
              >
                View All Trades
              </Button>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Active Strategies
            </Typography>
            {activeStrategies.map((strategy) => (
              <Card key={strategy.id} variant="outlined" sx={{ mb: 2 }}>
                <CardContent sx={{ py: 1.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle1">{strategy.name}</Typography>
                    <Chip
                      label={strategy.status}
                      size="small"
                      color={
                        strategy.status === "Running" ? "success" : "default"
                      }
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      color: "text.secondary",
                    }}
                  >
                    <Typography variant="body2">
                      <AccessTimeIcon
                        fontSize="small"
                        sx={{ verticalAlign: "middle", mr: 0.5 }}
                      />
                      {strategy.uptime}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={
                        strategy.performance.includes("+")
                          ? "success.main"
                          : "error.main"
                      }
                    >
                      {strategy.performance.includes("+") ? (
                        <TrendingUpIcon
                          fontSize="small"
                          sx={{ verticalAlign: "middle", mr: 0.5 }}
                        />
                      ) : (
                        <TrendingDownIcon
                          fontSize="small"
                          sx={{ verticalAlign: "middle", mr: 0.5 }}
                        />
                      )}
                      {strategy.performance}
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions sx={{ justifyContent: "flex-end", p: 1 }}>
                  <Button
                    size="small"
                    onClick={() => navigate(`/user/strategy-builder`)}
                  >
                    Details
                  </Button>
                  <Button size="small" color="primary">
                    Settings
                  </Button>
                </CardActions>
              </Card>
            ))}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <Button
                variant="text"
                endIcon={<AutoGraphIcon />}
                onClick={() => navigate("/user/strategy-builder")}
              >
                View All Strategies
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Trade Execution Section - Real-time trading activity */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TradeExecutionWidget />
        </Grid>
      </Grid>

      {/* Support Ticket Dialog */}
      <Dialog
        open={openTicketDialog}
        onClose={() => setOpenTicketDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6">Create Support Ticket</Typography>
            <IconButton onClick={() => setOpenTicketDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            Our support team is here to help you with any questions or issues
            you may have.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            name="subject"
            label="Subject"
            type="text"
            fullWidth
            variant="outlined"
            value={newTicket.subject}
            onChange={handleTicketChange}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="description"
            label="Description"
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            value={newTicket.description}
            onChange={handleTicketChange}
            placeholder="Please describe your issue in detail..."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setOpenTicketDialog(false)}>Cancel</Button>
          <Button
            onClick={handleTicketSubmit}
            variant="contained"
            disabled={!newTicket.subject || !newTicket.description}
          >
            Submit Ticket
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserDashboard;
